/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { AuthProvider } from "@/contexts/AuthContext";
import { initSupabase } from "@/lib/supabase";
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";

// Noisy exceptions to suppress (not actionable)
const SUPPRESSED_EXCEPTIONS = [
  // Browser bugs
  "ResizeObserver loop",
  "ResizeObserver loop completed with undelivered notifications",
  // Network errors from cancelled requests (user navigated away)
  "AbortError",
  "The operation was aborted",
  "Failed to fetch", // Often user navigated away or network blip
  // Stale chunks after deployment (user can refresh)
  "ChunkLoadError",
  "Loading chunk",
  // Browser extension errors (not our code)
  "moz-extension://",
  "chrome-extension://",
  "safari-extension://",
];

// Initialize PostHog once, outside of React render cycle
// Per docs: "Do not directly import posthog apart from installation"
// Disable in development to reduce console noise
const isDevelopment = window.location.hostname === "localhost" || window.location.hostname === "0.0.0.0";

async function initializePostHog(): Promise<void> {
  if (isDevelopment) {
    // Intentionally disabled in development to reduce console noise.
    return;
  }

  try {
    const response = await fetch("/api/posthog-config", {
      cache: "no-store",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      console.warn("[PostHog] Config endpoint returned non-200 status:", response.status);
      return;
    }

    const posthogConfig = (await response.json()) as { apiKey?: string; apiHost?: string };
    if (!posthogConfig.apiKey) {
      console.warn("[PostHog] No API key found - PostHog disabled");
      return;
    }

    posthog.init(posthogConfig.apiKey, {
      api_host: posthogConfig.apiHost || "https://us.i.posthog.com",
      ui_host: "https://us.posthog.com",
      defaults: "2025-11-30",
      // autocapture and capture_pageview are enabled by default
      // Session replay is controlled via PostHog project settings
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
      },
      // Filter noisy exceptions per PostHog docs
      // https://posthog.com/docs/error-tracking/capture-exceptions#suppressing-exceptions
      before_send: (event) => {
        if (event?.event === "$exception") {
          const exceptionList = event.properties?.["$exception_list"] || [];
          const exception = exceptionList.length > 0 ? exceptionList[0] : null;
          if (exception) {
            const type = exception.type || "";
            const value = exception.value || "";
            const combined = `${type}: ${value}`;
            // Check if this exception should be suppressed
            if (SUPPRESSED_EXCEPTIONS.some((pattern) => combined.includes(pattern))) {
              return null; // Drop this event
            }
          }
        }
        return event;
      },
    });
  } catch (error) {
    console.error("[PostHog] Initialization failed:", error);
  }
}

// Warm the auth config request before the first protected route asks for it.
void initSupabase().catch(() => {
  // AuthContext owns user-facing auth error handling.
});
void initializePostHog();
const elem = document.getElementById("root");

if (!elem) {
  console.error("[FATAL] Root element not found!");
  throw new Error("Root element #root not found");
}
const app = (
  <StrictMode>
    <PostHogProvider client={posthog}>
      <AuthProvider>
        <App />
      </AuthProvider>
    </PostHogProvider>
  </StrictMode>
);
createRoot(elem).render(app);
